import {useEffect} from 'react';
import {__} from '@wordpress/i18n';
import {registerPaymentMethod} from '@woocommerce/blocks-registry';
import {type CheckoutAfterProcessingWithErrorEventData} from '@woocommerce/checkout';
import {getSetting} from '@woocommerce/settings';
import {decodeEntities} from '@wordpress/html-entities';
import {createSetupIntent, loadStripe} from '../stripe';
import {type PaymentMethodInterface} from '@woocommerce/types';
import {startTransaction, updateTransaction, z} from '@peachpay/checkout-blocks';
import CurrencyFallback from '../../../../../blocks/src/currency-fallback';
import {parsePaymentData} from '../stripe';
import {getErrorMessage} from '../../../../../../frontend/@shared/ts/error';

const SettingsSchema = z.object({
	connect_id: z.string(),
	currency_fallback_required: z.string().nullable(),
	description: z.string(),
	icon_url: z.string(),
	public_key: z.string(),
	setup_intent_nonce: z.string(),
	setup_intent_url: z.string(),
	supports: z.string().array(),
	title: z.string(),
});

const schemaResult = SettingsSchema.safeParse(getSetting('peachpay_stripe_achdebit_data', false));

if (!schemaResult.success) {
	throw new Error(
		`PeachPay Stripe ACH Debit data is not available \n\n${schemaResult.error.message}`,
	);
}

const method_data = schemaResult.data;

const title = decodeEntities(method_data.title);
const description = decodeEntities(method_data.description);

loadStripe(method_data.public_key, method_data.connect_id)
	.then(stripe => {
		if (stripe === null) {
			throw new Error(__('Stripe is null', 'peachpay-for-woocommerce'));
		}

		if (typeof stripe === 'string') {
			throw new Error(stripe);
		}

		/**
     * Content component
     */
		const Content = (context: PaymentMethodInterface) => {
			const {emitResponse, eventRegistration, billing} = context;
			const {onPaymentSetup, onCheckoutSuccess, onCheckoutFail} = eventRegistration;

			useEffect(() => {
				const unsubscribe = onPaymentSetup(async () => {
					try {
						const start_transaction = await startTransaction('peachpay_stripe_achdebit');

						if (start_transaction.error) {
							return {
								type: emitResponse.responseTypes.ERROR,
								message: getErrorMessage(start_transaction.error),
							};
						}

						if (start_transaction.result === undefined) {
							return {
								type: emitResponse.responseTypes.ERROR,
								message: __('Missing transaction ID', 'peachpay-for-woocommerce'),
							};
						}

						const transaction_ID = start_transaction.result;

						const setupIntentResult = await createSetupIntent({
							payment_method_type: 'us_bank_account',
							setup_intent_url: method_data.setup_intent_url,
							setup_intent_nonce: method_data.setup_intent_nonce,
						});

						if (setupIntentResult === null) {
							const note = __('Failed to create setup intent.', 'peachpay-for-woocommerce');
							await updateTransaction(transaction_ID, {paymentStatus: 'creationfailed', note});
							return {
								type: emitResponse.responseTypes.ERROR,
								message: note,
							};
						}

						const collectionResult = await stripe.collectBankAccountForSetup({
							clientSecret: setupIntentResult.setup_intent_details.client_secret,
							params: {
								payment_method_type: 'us_bank_account',
								payment_method_data: {
									billing_details: {
										email: billing.billingAddress.email,
										name:
                      billing.billingAddress.first_name + ' ' + billing.billingAddress.last_name,
									},
								},
							},
						});

						if (collectionResult.error || !collectionResult.setupIntent) {
							const note = __('Failed to collect bank account.', 'peachpay-for-woocommerce');
							await updateTransaction(transaction_ID, {paymentStatus: 'creationfailed', note});
							return {
								type: emitResponse.responseTypes.ERROR,
								message: note,
							};
						}

						if (collectionResult.setupIntent.status !== 'requires_confirmation') {
							const note = __('Cancelled choosing a bank account.', 'peachpay-for-woocommerce');
							await updateTransaction(transaction_ID, {paymentStatus: 'creationfailed', note});
							return {
								type: emitResponse.responseTypes.ERROR,
								message: note,
							};
						}

						const confirmationResult = await stripe.confirmUsBankAccountSetup(
							setupIntentResult.setup_intent_details.client_secret,
						);

						if (confirmationResult.error || !confirmationResult.setupIntent) {
							const note = __('Failed to confirm setup intent.', 'peachpay-for-woocommerce');
							await updateTransaction(transaction_ID, {paymentStatus: 'creationfailed', note});
							return {
								type: emitResponse.responseTypes.ERROR,
								message: note,
							};
						}

						if (confirmationResult.setupIntent.status !== 'succeeded') {
							const note
                = __('Setup intent resulted in an invalid status', 'peachpay-for-woocommerce')
                + `: ${confirmationResult.setupIntent.status}`;
							await updateTransaction(transaction_ID, {paymentStatus: 'creationfailed', note});
							return {
								type: emitResponse.responseTypes.ERROR,
								message: note,
							};
						}

						if (
							typeof confirmationResult.setupIntent.payment_method === 'object'
              || confirmationResult.setupIntent.payment_method === null
						) {
							const note
                = __('Payment method invalid type:', 'peachpay-for-woocommerce')
                + `(${typeof confirmationResult.setupIntent.payment_method})`;
							await updateTransaction(transaction_ID, {paymentStatus: 'creationfailed', note});
							return {
								type: emitResponse.responseTypes.ERROR,
								message: note,
							};
						}

						return {
							type: emitResponse.responseTypes.SUCCESS,
							meta: {
								paymentMethodData: {
									peachpay_transaction_id: transaction_ID,
									peachpay_stripe_payment_method_id: confirmationResult.setupIntent.payment_method,
								},
							},
						};
					} catch (error) {
						return {
							type: emitResponse.responseTypes.ERROR,
							message: getErrorMessage(error),
						};
					}
				});

				return unsubscribe;
			}, [billing.billingAddress, onPaymentSetup]);

			useEffect(() => {
				const unsubscribe = onCheckoutSuccess(async (data: {redirectUrl: string}) => {
					const dataURL = new URL(data.redirectUrl);

					const [name, encodedStringData] = dataURL.hash.split('=');

					if (dataURL.hash === '' || name !== '#payment_data' || !encodedStringData) {
						return;
					}

					const paymentData = parsePaymentData(encodedStringData);

					if (paymentData === null) {
						return {
							type: emitResponse.responseTypes.ERROR,
							message: __('Failed to parse payment data', 'peachpay-for-woocommerce'),
						};
					}

					const transaction_ID = paymentData.transaction_id;
					const clientSecret = paymentData.data.client_secret;
					const successURL = paymentData.success_url;

					const {paymentIntent, error} = await stripe.retrievePaymentIntent(clientSecret);

					if (error) {
						const message
              = error?.message
              ?? __('Failed to retrieve payment intent.', 'peachpay-for-woocommerce');

						await updateTransaction(transaction_ID, {note: message});
						return {
							type: emitResponse.responseTypes.ERROR,
							message,
							messageContext: emitResponse.noticeContexts.PAYMENTS,
						};
					}

					if (paymentIntent) {
						return {
							type: emitResponse.responseTypes.SUCCESS,
							redirectUrl: successURL,
						};
					}

					const message = __('Payment intent missing', 'peachpay-for-woocommerce');
					await updateTransaction(transaction_ID, {note: message});
					return {
						type: emitResponse.responseTypes.ERROR,
						message,
						messageContext: emitResponse.noticeContexts.PAYMENTS,
					};
				});

				return unsubscribe;
			}, [onCheckoutSuccess]);

			useEffect(() => {
				const unsubscribe = onCheckoutFail((data: CheckoutAfterProcessingWithErrorEventData) => {
					if (data.processingResponse?.paymentStatus === emitResponse.responseTypes.FAIL) {
						return {
							type: emitResponse.responseTypes.FAIL,
							message: data.processingResponse.paymentDetails['message'],
							messageContext: emitResponse.noticeContexts.PAYMENTS,
						};
					}

					return {
						type: emitResponse.responseTypes.FAIL,
						message:
              data.processingResponse?.message
              ?? __('Checkout failed unexpectedly', 'peachpay-for-woocommerce')
                + `${JSON.stringify(data)}`,
						messageContext: emitResponse.noticeContexts.PAYMENTS,
					};
				});

				return unsubscribe;
			}, [onCheckoutFail]);

			if (method_data.currency_fallback_required) {
				return <CurrencyFallback name={title} currency={method_data.currency_fallback_required} />;
			}

			return (
				<>
					<img style={{maxHeight: '25px'}} src={method_data.icon_url} />
					<p style={{textAlign: 'left', margin: '0.5rem 0 0'}}>{description}</p>

					<Mandate />
				</>
			);
		};

		/**
     * Label component
     *
     * @param {PaymentMethodInterface} props Props from payment API.
     */
		const Label = (props: PaymentMethodInterface) => {
			const {PaymentMethodLabel, PaymentMethodIcons} = props.components;
			return (
				<>
					<PaymentMethodLabel text={title} />
					<PaymentMethodIcons
						className='pp-label-icon'
						icons={[
							{
								id: 'peachpay_stripe_achdebit',
								src: method_data.icon_url,
								alt: title,
							},
						]}
					/>
				</>
			);
		};

		registerPaymentMethod({
			ariaLabel: title,
			canMakePayment(data) {
				return data.paymentMethods.includes('peachpay_stripe_achdebit');
			},
			// @ts-expect-error Missing types are expected because WooCommerce gives the props
			content: <Content />,
			// @ts-expect-error Missing types are expected because WooCommerce gives the props
			edit: <Content />,
			// @ts-expect-error Missing types are expected because WooCommerce gives the props
			label: <Label />,
			name: 'peachpay_stripe_achdebit',
			supports: {
				features: method_data.supports,
				showSavedCards: false,
				showSaveOption: false,
			},
		});
	})
	.catch(error => {
		throw new Error(getErrorMessage(error));
	});

function Mandate() {
	const hostname = window.top?.location.hostname;

	return (
		<>
			<hr style={{margin: '0.5rem 0'}}/>
			<p>
        By placing an order, you authorize <strong>{hostname}</strong> to debit the bank account
        specified for any amount owed for charges arising from your use of{' '}
				<strong>{hostname}</strong>&apos;s services and/or purchase of products from{' '}
				<strong>{hostname}</strong>, pursuant to <strong>{hostname}</strong>&apos;s website and
        terms, until this authorization is revoked. You may amend or cancel this authorization at
        any time by providing notice to <strong>{hostname}</strong> with 30 (thirty) days notice.
			</p>
		</>
	);
}
